/**
 * Portfolio Analytics (`a.js`, the owner's central multi-app dashboard),
 * now strictly CONSENT-GATED.
 *
 * Why gated (decision M5, see docs/ANALYTICS.md "Portfolio tracker"):
 * despite its "cookieless" label the third-party script writes a session id,
 * a last-seen timestamp and a pending-event queue to localStorage and the user
 * id to sessionStorage, and has no storage-free mode we can switch on. Writing
 * to the device for non-essential reach measurement needs prior consent under
 * ePrivacy art. 5(3) (EU), PECR reg. 6 (UK) and is expected by the Swiss FDPIC
 * guidance, so the script only loads after analytics consent, and a withdrawal
 * stops it and deletes everything it stored.
 */
import {
  ANALYTICS_APP_ID,
  ANALYTICS_INGEST_URL,
  ANALYTICS_KEY,
  ANALYTICS_SRC,
} from '../analytics-config'

export const PORTFOLIO_SCRIPT_ID = 'portfolio-analytics'

/** Web storage keys written by `a.js` (verified against the shipped script). */
export const PORTFOLIO_LOCAL_STORAGE_KEYS = [
  'analytics_session_id',
  'analytics_session_last',
] as const
export const PORTFOLIO_LOCAL_STORAGE_PREFIX = 'analytics_pending_events:'
export const PORTFOLIO_SESSION_STORAGE_KEYS = ['analytics_user_id'] as const

type PortfolioEngine = { stop?: () => void }
type PortfolioWindow = Window & { __revealAnalyticsEngine?: PortfolioEngine }

function hasDom(): boolean {
  return typeof window !== 'undefined' && typeof document !== 'undefined'
}

/** Inject the tracker (idempotent). Call ONLY with analytics consent. */
export function loadPortfolioTracker(): void {
  if (!hasDom()) return
  if (document.getElementById(PORTFOLIO_SCRIPT_ID)) return
  const script = document.createElement('script')
  script.id = PORTFOLIO_SCRIPT_ID
  script.defer = true
  script.src = ANALYTICS_SRC
  script.setAttribute('data-ingest-key', ANALYTICS_KEY)
  script.setAttribute('data-ingest-url', ANALYTICS_INGEST_URL)
  script.setAttribute('data-app-id', ANALYTICS_APP_ID)
  document.head.appendChild(script)
}

/** Delete every key the tracker may have written to web storage. */
export function clearPortfolioStorage(): void {
  if (!hasDom()) return
  try {
    for (const key of PORTFOLIO_LOCAL_STORAGE_KEYS) window.localStorage.removeItem(key)
    const stale: Array<string> = []
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i)
      if (key?.startsWith(PORTFOLIO_LOCAL_STORAGE_PREFIX)) stale.push(key)
    }
    for (const key of stale) window.localStorage.removeItem(key)
  } catch {
    // storage blocked
  }
  try {
    for (const key of PORTFOLIO_SESSION_STORAGE_KEYS) window.sessionStorage.removeItem(key)
  } catch {
    // storage blocked
  }
}

/** Consent withdrawn: stop the engine, remove the script and its storage. */
export function unloadPortfolioTracker(): void {
  if (!hasDom()) return
  const win = window as PortfolioWindow
  try {
    win.__revealAnalyticsEngine?.stop?.()
  } catch {
    // ignore
  }
  delete win.__revealAnalyticsEngine
  win.analytics = undefined
  document.getElementById(PORTFOLIO_SCRIPT_ID)?.remove()
  clearPortfolioStorage()
}

/** Forward a product event (no-op unless the consented tracker is loaded). */
export function portfolioTrack(name: string, props: Record<string, unknown>): void {
  if (!hasDom()) return
  window.analytics?.track(name, props)
}

/** Attribute events to the signed-in Clerk user (consented tracker only). */
export function portfolioIdentify(userId: string): void {
  if (!hasDom()) return
  if (window.analytics) {
    window.analytics.identify(userId)
    return
  }
  // The script loads async: identify as soon as it announces readiness.
  window.addEventListener(
    'revealit:analytics-ready',
    () => window.analytics?.identify(userId),
    { once: true },
  )
}
